/**
 * Automation query key factories.
 */

export const automationsQueryKeys = {
  all: ['automations'] as const,
  lists: () => [...automationsQueryKeys.all, 'list'] as const,
  list: (filters: AutomationsListFilters) =>
    [
      ...automationsQueryKeys.lists(),
      filters.searchQuery,
      filters.pageSize,
      filters.filtersString,
      filters.sort,
      filters.order,
      filters.extraFiltersKey,
    ] as const,
  details: () => [...automationsQueryKeys.all, 'detail'] as const,
  detail: (automationId: string) => [...automationsQueryKeys.details(), automationId] as const,
  /** Spine fetch for project Automations tab step chain (Phase 3.2). */
  stepChain: (automationId: string) =>
    [...automationsQueryKeys.details(), 'step-chain', automationId] as const,
};

/** Stable filters for query key (no page; page is internal to infinite/hydrated list queries) */
export interface AutomationsListFilters {
  searchQuery: string;
  pageSize: number;
  filtersString: string | undefined;
  sort: string;
  order: 'asc' | 'desc';
  enabled: boolean;
  /** Extra filter keys passed to API (serialized for key stability) */
  extraFiltersKey: string;
  extraFilters: Record<string, unknown>;
}

export const automationTemplatesQueryKeys = {
  all: ['automation-templates'] as const,
  list: () => [...automationTemplatesQueryKeys.all, 'list'] as const,
};

export const executionsQueryKeys = {
  all: ['executions'] as const,
  lists: () => [...executionsQueryKeys.all, 'list'] as const,
  list: (automationId: string) => [...executionsQueryKeys.lists(), automationId] as const,
  details: () => [...executionsQueryKeys.all, 'detail'] as const,
  detail: (executionId: string) => [...executionsQueryKeys.details(), executionId] as const,
};

export interface ApprovalsListFilters {
  projectId?: string;
  pendingOnly?: boolean;
}

export const approvalsQueryKeys = {
  all: ['automation-approvals'] as const,
  lists: () => [...approvalsQueryKeys.all, 'list'] as const,
  list: (filters: ApprovalsListFilters = {}) =>
    [
      ...approvalsQueryKeys.lists(),
      filters.projectId ?? 'all',
      filters.pendingOnly === false ? 'all-statuses' : 'pending',
    ] as const,
  details: () => [...approvalsQueryKeys.all, 'detail'] as const,
  detail: (approvalId: string) => [...approvalsQueryKeys.details(), approvalId] as const,
  queue: () => [...approvalsQueryKeys.all, 'queue', 'mine'] as const,
  histories: () => [...approvalsQueryKeys.all, 'history'] as const,
  history: (filters: { projectId?: string; automationId?: string }) =>
    [
      ...approvalsQueryKeys.histories(),
      filters.projectId ?? 'no-project',
      filters.automationId ?? 'no-automation',
    ] as const,
  analytics: () => [...approvalsQueryKeys.all, 'analytics'] as const,
};

export const automationNodeConfigQueryKeys = {
  all: [...automationsQueryKeys.all, 'node-config'] as const,
  sendDocumentList: () => [...automationNodeConfigQueryKeys.all, 'send-document-list'] as const,
  fileCompletedTemplates: (fileType: string) =>
    [...automationNodeConfigQueryKeys.all, 'file-completed-templates', fileType] as const,
};
